import { motion } from "motion/react"
import { ArrowLeft, Check, ChevronRight } from "@untitledui/icons"
import { PrimaryButton } from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"
import { DAMAGE_MARK_COPY, DAMAGE_ZONE_COPY } from "./copy"
import type { DamageZone } from "./types"

void motion

// Quick Estimate 2 — Step 2.5 (between photos and send).
// Tap-and-mark damage zones on a top-down vehicle silhouette.
// Replaces typing damage descriptions with one tap per panel.
//
// The user can skip entirely (no zones marked = "we don't know
// exactly where, see photos"). Most won't skip — pointing at the
// dent is faster than describing it.

interface StepDamageMarkProps {
  selected: DamageZone[]
  onToggle: (zone: DamageZone) => void
  onBack: () => void
  onContinue: () => void
}

export function StepDamageMark({
  selected,
  onToggle,
  onBack,
  onContinue,
}: StepDamageMarkProps) {
  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          {DAMAGE_MARK_COPY.eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {DAMAGE_MARK_COPY.title}
        </h2>
        <p className="text-[15px] text-zinc-500 mt-2 leading-relaxed">
          {DAMAGE_MARK_COPY.blurb}
        </p>
      </header>

      {/* Top-down vehicle map */}
      <div className="rounded-2xl border border-zinc-200 bg-gradient-to-b from-sky-50/30 via-white to-zinc-100 overflow-hidden p-3 sm:p-6">
        <VehicleTopDown selected={selected} onToggle={onToggle} />
      </div>

      {/* Selected count */}
      <div className="flex items-center justify-between">
        <p className="text-[12.5px] font-medium text-zinc-500">
          {DAMAGE_MARK_COPY.zoneCount(selected.length)}
        </p>
        {selected.length === 0 && (
          <p className="text-[11px] text-zinc-400">
            {DAMAGE_MARK_COPY.noneSelected}
          </p>
        )}
      </div>

      {/* Selected pills (so the user can review/dismiss without finding the panel again) */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => onToggle(z)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors duration-150"
            >
              <Check className="size-3" aria-hidden />
              {DAMAGE_ZONE_COPY[z]}
              <span className="text-emerald-500">×</span>
            </button>
          ))}
        </div>
      )}

      <div className="space-y-2 pt-2">
        <PrimaryButton onClick={onContinue} type="button" size="wide">
          {selected.length === 0
            ? "Skip — no specific damage"
            : DAMAGE_MARK_COPY.continue}
          <ChevronRight className="size-4" aria-hidden />
        </PrimaryButton>
        <button
          type="button"
          onClick={onBack}
          className="w-full h-10 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Back to photos
        </button>
      </div>
    </div>
  )
}

/* ── Top-down vehicle map ──────────────────────── */
//
// A simple sedan top-down silhouette with tappable polygon zones.
// Selected zones get an emerald fill + check; unselected use the
// body silver/grey gradient. Tap = toggle. Each polygon is a real
// <button> for accessibility.

function VehicleTopDown({
  selected,
  onToggle,
}: {
  selected: DamageZone[]
  onToggle: (z: DamageZone) => void
}) {
  const isSel = (z: DamageZone) => selected.includes(z)
  // Compact 4:5 aspect — much shorter than the previous 5:9. Panel
  // seams render as always-visible thin dashed lines so the user
  // sees where they can tap before they tap. Tiny labels stay
  // visible on every zone — discoverability over minimalism.
  return (
    <div className="relative w-full max-w-[360px] mx-auto aspect-[4/5]">
      <svg
        viewBox="0 0 220 280"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0 w-full h-full"
        role="group"
        aria-label="Tap a panel to mark damage"
      >
        <defs>
          <linearGradient id="topBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fafafa" />
            <stop offset="1" stopColor="#e4e4e7" />
          </linearGradient>
          <linearGradient id="topGlass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#475569" stopOpacity="0.85" />
            <stop offset="1" stopColor="#1e293b" stopOpacity="0.85" />
          </linearGradient>
        </defs>

        {/* Body outline — sedan top-down, slightly narrower for a
            more car-shaped silhouette. Coords picked so panels carve
            cleanly into named zones below. */}
        <path
          d="M 60 22
             Q 60 8 78 6
             Q 110 2 142 6
             Q 160 8 160 22
             L 160 258
             Q 160 272 142 274
             Q 110 278 78 274
             Q 60 272 60 258
             Z"
          fill="url(#topBody)"
          stroke="#52525b"
          strokeWidth="1.4"
        />

        {/* Visual hints so the body reads as a car: side mirrors,
            head/tail-light slivers, four wheels seen from above as
            small rounded rectangles tucked under the wheel arches.
            All non-interactive (pointerEvents:none). */}
        <g pointerEvents="none">
          {/* Side mirrors — small protrusions just past the front
              fenders on each side. */}
          <path d="M 56 38 L 50 42 Q 47 44 51 47 L 60 44 Z" fill="#e4e4e7" stroke="#71717a" strokeWidth="0.6" />
          <path d="M 164 38 L 170 42 Q 173 44 169 47 L 160 44 Z" fill="#e4e4e7" stroke="#71717a" strokeWidth="0.6" />
          {/* Headlight slivers */}
          <path d="M 70 12 L 88 10 L 88 16 L 70 18 Z" fill="none" stroke="#71717a" strokeWidth="0.5" />
          <path d="M 132 10 L 150 12 L 150 18 L 132 16 Z" fill="none" stroke="#71717a" strokeWidth="0.5" />
          {/* Tail-light slivers */}
          <path d="M 70 268 L 88 270 L 88 264 L 70 262 Z" fill="none" stroke="#71717a" strokeWidth="0.5" />
          <path d="M 132 270 L 150 268 L 150 262 L 132 264 Z" fill="none" stroke="#71717a" strokeWidth="0.5" />
          {/* Wheels — top-down view: small rounded rectangles on
              each corner. Sit just under the body edges to read as
              tires peeking out from wheel wells. */}
          <rect x="48" y="48" width="9" height="22" rx="2" fill="#3f3f46" opacity="0.55" />
          <rect x="163" y="48" width="9" height="22" rx="2" fill="#3f3f46" opacity="0.55" />
          <rect x="48" y="208" width="9" height="22" rx="2" fill="#3f3f46" opacity="0.55" />
          <rect x="163" y="208" width="9" height="22" rx="2" fill="#3f3f46" opacity="0.55" />
        </g>

        {/* Roof glass band (windshield → roof → rear window). Drawn
            BELOW the click targets so the dark glass shows through
            the roof/windshield zones when not selected. */}
        <path
          d="M 70 70
             Q 70 62 82 60
             L 138 60
             Q 150 62 150 70
             L 150 200
             Q 150 208 138 210
             L 82 210
             Q 70 208 70 200
             Z"
          fill="url(#topGlass)"
          pointerEvents="none"
        />

        {/* ── Always-visible panel seams (thin dashed) ──
            Tells the user where panels divide before they tap. */}
        <g
          stroke="#52525b"
          strokeWidth="0.6"
          strokeDasharray="2 2"
          opacity="0.55"
          pointerEvents="none"
        >
          {/* Hood seam (between hood and windshield) */}
          <line x1="60" y1="60" x2="160" y2="60" />
          {/* Front fender separators (between hood and doors) */}
          <line x1="70" y1="22" x2="70" y2="60" />
          <line x1="150" y1="22" x2="150" y2="60" />
          {/* B-pillar (between front and rear doors) */}
          <line x1="60" y1="135" x2="160" y2="135" />
          {/* Rear door / quarter panel seam */}
          <line x1="60" y1="200" x2="160" y2="200" />
          {/* Trunk seam */}
          <line x1="60" y1="210" x2="160" y2="210" />
          {/* Rear bumper seam */}
          <line x1="60" y1="248" x2="160" y2="248" />
          {/* Body-side seams (door edges) */}
          <line x1="70" y1="60" x2="70" y2="248" />
          <line x1="150" y1="60" x2="150" y2="248" />
        </g>

        {/* Tappable zones — drawn AFTER the seams, with always-on
            text labels so the user can read what they're about to
            tap. Selected fills emerald; unselected stay transparent
            but still register clicks. */}

        {/* Hood */}
        <Zone
          zone="hood"
          d="M 60 22 Q 60 8 78 6 Q 110 2 142 6 Q 160 8 160 22 L 160 60 L 60 60 Z"
          selected={isSel("hood")}
          label="Hood"
          labelX={110}
          labelY={36}
          onClick={() => onToggle("hood")}
        />

        {/* Windshield (top of glass) */}
        <Zone
          zone="windshield"
          d="M 70 60 L 150 60 L 150 78 L 70 78 Z"
          selected={isSel("windshield")}
          label="Windshield"
          labelX={110}
          labelY={71}
          glass
          onClick={() => onToggle("windshield")}
        />

        {/* Roof — center of glass band */}
        <Zone
          zone="roof"
          d="M 70 78 L 150 78 L 150 192 L 70 192 Z"
          selected={isSel("roof")}
          label="Roof"
          labelX={110}
          labelY={134}
          glass
          onClick={() => onToggle("roof")}
        />

        {/* Front fenders — left/right strips beside hood */}
        <Zone
          zone="front-fender-left"
          d="M 60 22 Q 60 8 78 6 L 78 60 L 60 60 Z"
          selected={isSel("front-fender-left")}
          label="L. fender"
          labelX={68}
          labelY={42}
          onClick={() => onToggle("front-fender-left")}
        />
        <Zone
          zone="front-fender-right"
          d="M 142 6 Q 160 8 160 22 L 160 60 L 142 60 Z"
          selected={isSel("front-fender-right")}
          label="R. fender"
          labelX={152}
          labelY={42}
          onClick={() => onToggle("front-fender-right")}
        />

        {/* Front doors */}
        <Zone
          zone="front-door-left"
          d="M 60 60 L 70 60 L 70 135 L 60 135 Z"
          selected={isSel("front-door-left")}
          label="L. door"
          labelX={66}
          labelY={100}
          onClick={() => onToggle("front-door-left")}
          rotated
        />
        <Zone
          zone="front-door-right"
          d="M 150 60 L 160 60 L 160 135 L 150 135 Z"
          selected={isSel("front-door-right")}
          label="R. door"
          labelX={155}
          labelY={100}
          onClick={() => onToggle("front-door-right")}
          rotated
        />

        {/* Rear doors */}
        <Zone
          zone="rear-door-left"
          d="M 60 135 L 70 135 L 70 200 L 60 200 Z"
          selected={isSel("rear-door-left")}
          label="L. rear"
          labelX={66}
          labelY={170}
          onClick={() => onToggle("rear-door-left")}
          rotated
        />
        <Zone
          zone="rear-door-right"
          d="M 150 135 L 160 135 L 160 200 L 150 200 Z"
          selected={isSel("rear-door-right")}
          label="R. rear"
          labelX={155}
          labelY={170}
          onClick={() => onToggle("rear-door-right")}
          rotated
        />

        {/* Quarter panels */}
        <Zone
          zone="quarter-panel-left"
          d="M 60 200 L 70 200 L 70 248 L 60 248 Z"
          selected={isSel("quarter-panel-left")}
          label="L. quarter"
          labelX={66}
          labelY={224}
          onClick={() => onToggle("quarter-panel-left")}
          rotated
        />
        <Zone
          zone="quarter-panel-right"
          d="M 150 200 L 160 200 L 160 248 L 150 248 Z"
          selected={isSel("quarter-panel-right")}
          label="R. quarter"
          labelX={155}
          labelY={224}
          onClick={() => onToggle("quarter-panel-right")}
          rotated
        />

        {/* Trunk */}
        <Zone
          zone="trunk"
          d="M 70 210 L 150 210 L 150 248 L 70 248 Z"
          selected={isSel("trunk")}
          label="Trunk"
          labelX={110}
          labelY={230}
          onClick={() => onToggle("trunk")}
        />

        {/* Rear bumper */}
        <Zone
          zone="rear-bumper"
          d="M 60 248 L 160 248 L 160 258 Q 160 272 142 274 Q 110 278 78 274 Q 60 272 60 258 Z"
          selected={isSel("rear-bumper")}
          label="Rear bumper"
          labelX={110}
          labelY={264}
          onClick={() => onToggle("rear-bumper")}
        />

        {/* Front bumper — sits under the front nose curve */}
        <Zone
          zone="front-bumper"
          d="M 60 22 Q 60 8 78 6 Q 110 2 142 6 Q 160 8 160 22 L 160 14 Q 160 0 142 -2 Q 110 -6 78 -2 Q 60 0 60 14 Z"
          selected={isSel("front-bumper")}
          label="Front bumper"
          labelX={110}
          labelY={14}
          onClick={() => onToggle("front-bumper")}
        />

        {/* Static side labels — always visible so users orient
            themselves. Lighter weight so they don't compete with
            the panel labels. */}
        <text
          x="32"
          y="140"
          textAnchor="middle"
          fontSize="8"
          fontWeight="700"
          letterSpacing="2"
          fill="#a1a1aa"
          transform="rotate(-90 32 140)"
          pointerEvents="none"
        >
          DRIVER
        </text>
        <text
          x="190"
          y="140"
          textAnchor="middle"
          fontSize="8"
          fontWeight="700"
          letterSpacing="2"
          fill="#a1a1aa"
          transform="rotate(90 190 140)"
          pointerEvents="none"
        >
          PASSENGER
        </text>
      </svg>
    </div>
  )
}

function Zone({
  zone,
  d,
  selected,
  label,
  labelX,
  labelY,
  onClick,
  rotated,
}: {
  zone: DamageZone
  d: string
  selected: boolean
  label: string
  labelX?: number
  labelY?: number
  onClick: () => void
  glass?: boolean
  rotated?: boolean
}) {
  // Labels render only when the zone is selected, or while hovered
  // (group-hover from the wrapping <g class="group">). Keeps the
  // diagram quiet by default and surfaces labels on intent.
  return (
    <g className="group">
      {/* Selected fill — emerald wash with subtle stroke */}
      {selected && (
        <path
          d={d}
          fill="rgba(16, 185, 129, 0.4)"
          stroke="#10b981"
          strokeWidth="1.5"
          pointerEvents="none"
        />
      )}
      {/* Click target */}
      <path
        d={d}
        fill="rgba(255,255,255,0)"
        stroke="rgba(0,0,0,0)"
        strokeWidth="0"
        className={cx(
          "cursor-pointer transition-all duration-150",
          !selected && "group-hover:fill-zinc-900/[0.08]",
        )}
        onClick={onClick}
        role="button"
        aria-label={`Mark ${zone}`}
        aria-pressed={selected}
      />
      {/* Label */}
      {label && labelX != null && labelY != null && (
        <text
          x={labelX}
          y={labelY}
          textAnchor="middle"
          fontSize={rotated ? "6.5" : "7.5"}
          fontWeight={selected ? "700" : "600"}
          fill={selected ? "#065f46" : "#52525b"}
          letterSpacing="0.3"
          transform={rotated ? `rotate(-90 ${labelX} ${labelY})` : undefined}
          pointerEvents="none"
          className={cx(
            "transition-opacity duration-150",
            !selected && "opacity-0 group-hover:opacity-90",
          )}
        >
          {label}
        </text>
      )}
    </g>
  )
}
